import { orderBy } from 'firebase/firestore';
import { values } from 'ramda';
import { ofType } from 'redux-observable';
import { Observable, from, of } from 'rxjs';
import { catchError, map, mergeMap, withLatestFrom } from 'rxjs/operators';
import { Suppliers } from '@bridebook/models';
import { TranslatedError } from '@bridebook/toolbox/src';
import { getExt } from '@bridebook/toolbox/src/getExt';
import {
  addSupplierFeedback,
  validateReviewFormFields,
} from '@bridebook/web/app-shared/lib/reviews/utils';
import { getI18n } from '@bridebook/web/lib/i18n/getI18n';
import { Action, IApplicationState, IEpicDeps } from '@bridebook/web/lib/types';
import { UrlHelper } from '@bridebook/web/lib/url-helper';
import { IMAGES_BASE_URL } from '@bridebook/toolbox/src/buildImageUrl';
import {
  clearSupplierDetails,
  fetchSupplierPhotosDone,
  saveReview,
  saveReviewError,
  saveReviewSuccess,
  uploadReviewPhotoError,
  uploadReviewPhotoSuccess,
} from './actions';
import { SupplierActions } from './actions-types';
import { getCurrentSupplier, getSupplierDataLoaded, getSupplierLoaded } from './selectors';
import { getImageDimensionsFromUrl } from '@bridebook/ui';

export const saveReviewEpic = (action$: Observable<Action>, { validate, state$ }: IEpicDeps) =>
  action$.pipe(
    ofType(SupplierActions.SAVE_REVIEW),
    withLatestFrom(state$),
    mergeMap(([action, state]: [Action, IApplicationState]) => {
      const { captchaToken } = (action as ReturnType<typeof saveReview>).payload;
      const {
        supplier: {
          reviewForm: { recaptchaComplete, fields },
        },
        users: { user },
      } = state;
      const i18n = getI18n();
      const supplier = getCurrentSupplier(state);

      if (!supplier || !supplier.id) {
        throw new TranslatedError(
          'Save review: invalid supplier!',
          i18n.t('supplier:form.review.error.invalidSupplier'),
        );
      }

      const getPromise = async () => {
        await validateReviewFormFields(fields, recaptchaComplete, validate);
        return addSupplierFeedback(supplier, fields, user?.id, captchaToken);
      };
      return from(getPromise()).pipe(
        map(saveReviewSuccess),
        catchError((error) => of(saveReviewError(error))),
      );
    }),
  );

export const uploadReviewPhotoEpic = (action$: Observable<Action>) =>
  action$.pipe(
    ofType(SupplierActions.UPLOAD_REVIEW_PHOTO),
    mergeMap(({ payload: { photo } }) => {
      const getPromise = async () => {
        const ext = getExt(photo.filename);
        /* eslint-disable */
        const public_id = photo.filename.replace(`.${ext}`, '');
        const imgixURL = `${IMAGES_BASE_URL}/${public_id}.${ext}`;
        /* eslint-enable */
        const result = await getImageDimensionsFromUrl(imgixURL);
        const { width, height } = result;

        return {
          portrait: height >= width,
          public_id,
          ext,
          imgixURL,
        };
      };
      return from(getPromise()).pipe(
        map(uploadReviewPhotoSuccess),
        catchError((error) => of(uploadReviewPhotoError(error))),
      );
    }),
  );

export const fetchSupplierPhotosEpic = (action$: Observable<Action>) =>
  action$.pipe(
    ofType(SupplierActions.FETCH_PHOTOS),
    mergeMap(({ payload: { supplierId } }) => {
      const promise = Suppliers._.getById(supplierId)
        .Photos.query([orderBy('order')])
        .get();

      return from(promise).pipe(
        mergeMap((data) => {
          const photos = values(data);
          return of(fetchSupplierPhotosDone(photos));
        }),
        catchError((error) =>
          of({
            type: SupplierActions.FETCH_SUPPLIER_PHOTOS_ERROR,
            payload: error,
          }),
        ),
      );
    }),
  );

export const registerSupplierCardInView = (action$: Observable<Action>) =>
  action$.pipe(
    ofType(SupplierActions.SUPPLIER_CARD_IN_VIEW),
    mergeMap(({ payload }) => {
      if (!process.browser) {
        return of();
      }

      return of({ type: SupplierActions.VIEWED_SUPPLIER_TILE_ANALYTICS, payload });
    }),
  );

export const leaveSupplierProfileEpic = (action$: Observable<Action>, { state$ }: IEpicDeps) =>
  action$.pipe(
    ofType('ROUTE_CHANGE_COMPLETE'),
    withLatestFrom(state$),
    mergeMap(
      ([
        {
          payload: { serverPage },
        },
        state,
      ]: [Action, IApplicationState]) => {
        if (
          (getSupplierLoaded(state) || getSupplierDataLoaded(state)) &&
          !serverPage.includes(UrlHelper.supplier)
        ) {
          return of(clearSupplierDetails());
        }

        return of();
      },
    ),
  );

export const pageEpics = [
  fetchSupplierPhotosEpic,
  saveReviewEpic,
  uploadReviewPhotoEpic,
  leaveSupplierProfileEpic,
];
